import { useApp, useBehavior, useTriggerAction, useUpdateWidgetValue, useUpdateWidgetValueViaAction } from '@libs/hooks';
import { useConsumerInput } from '../../hooks/useInputs';
import { ActionActionTypeEnum, AssetsSvgMap, TWidgetProps } from '@libs/types';
import React, { useId } from 'react';
import { TConsumerItem, TInputSearchAddress, TInputSearchAddressItem } from '../../types/data';
import { RowEnum } from '../../types/enums';
import { TInputOption } from '../../types/options';
import { convertItemToRowComponentProps } from '../../utils/helpers';
import { ButtonRowView } from '../Buttons/ButtonRowView';
import { TListItemProps } from '../List/ListItems';
import ListSearch from '../List/select/ListSearch';
import ItemListComponent from '../UI/Row/ItemListComponent';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import { Modal } from '../UI/Modal/Modal';

const InputSearchAddress = ({ widget }: TWidgetProps<TInputSearchAddress, TInputOption>) => {
    const id = useId();
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const { isFilled, isError, errorMessage, label, helperText, value, ref } = useConsumerInput(widget, {
        isHidden,
        isDisabled,
        triggerActionsByType,
        setModal,
    });
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { updateWidgetValueViaAction } = useUpdateWidgetValueViaAction({ triggerActionsByNameAndType });

    const renderItem = ({ item }: TListItemProps) => {
        return (
            item && (
                <ItemListComponent
                    key={item.id}
                    {...convertItemToRowComponentProps(item)}
                    handleClick={() => itemSelected(item as TInputSearchAddressItem)}
                    id={RowEnum.ROW_REGULAR}
                />
            )
        );
    };

    const selectCurrentValue = (item: TConsumerItem) => (
        <ButtonRowView onClick={() => itemSelected(item as TInputSearchAddressItem)} label={data.messages?.continueButtonTitle} />
    );

    const itemSelected = (item: TInputSearchAddressItem) => {
        updateWidgetValue(item?.clearTitle ?? item.value ?? '');
        if (item?.details) {
            Object.keys(item.details).forEach((key) => updateWidgetValueViaAction(key, ActionActionTypeEnum.onFill, item.details[key] as string));
        }
        setModal(null);
    };

    const modalContent = () => {
        return (
            <Modal isCanCloseModal={false} noTopPadding iconName={AssetsSvgMap.ic_arrow_back}>
                <ListSearch
                    widget={widget}
                    renderItem={renderItem}
                    closeModal={() => setModal(null)}
                    selectCurrentValue={selectCurrentValue}
                    minQueryLength={data.minQueryLength}
                    searchOnServer
                />
            </Modal>
        );
    };
    return (
        <InputWrapper
            isDropdownInput
            htmlFor={id}
            widget={widget}
            labelText={label}
            isError={isError}
            isFilled={isFilled}
            isDisabled={isDisabled}
            isHidden={isHidden}
            helperText={helperText}
            errorMessage={errorMessage}
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
        >
            <input
                ref={ref}
                id={id}
                value={value}
                readOnly={true}
                onClick={() => setModal(modalContent)}
                onFocus={() => setModal(modalContent)}
                data-test={widget.uid}
                type="text"
                tabIndex={data.tabIndex}
            />
        </InputWrapper>
    );
};

export { InputSearchAddress };
